import { supabase } from '../../supabase';

function isRpcMissing(error) {
  const msg = String(error?.message || error || '').toLowerCase();
  return (
    msg.includes('could not find the function')
    || msg.includes('function public.get_finance_ledger_page')
    || msg.includes('schema cache')
    || (msg.includes('get_finance_ledger_page') && msg.includes('does not exist'))
  );
}

const FINANCE_LEDGER_PAGE_SIZE = 5000;

export async function fetchFinanceLedgerPageRpc({
  gymId,
  page = 1,
  pageSize = FINANCE_LEDGER_PAGE_SIZE,
  allMatching = false,
} = {}) {
  if (!gymId) return null;

  const size = Math.min(20000, Math.max(10, Number(pageSize) || FINANCE_LEDGER_PAGE_SIZE));
  const { data, error } = await supabase.rpc('get_finance_ledger_page', {
    p_gym_id: gymId,
    p_page: allMatching ? 0 : Math.max(1, Number(page) || 1),
    p_page_size: allMatching ? 20000 : size,
  });

  if (error) {
    if (isRpcMissing(error)) return null;
    throw error;
  }
  const payload = typeof data === 'string'
    ? (() => { try { return JSON.parse(data); } catch { return null; } })()
    : data;
  if (!payload || payload.error) return null;

  return {
    totalPayments: Number(payload.total_payments) || 0,
    collected: Number(payload.collected) || 0,
    page: Number(payload.page) || 1,
    pageSize: Number(payload.page_size) || pageSize,
    payments: Array.isArray(payload.payments) ? payload.payments : [],
    expenses: Array.isArray(payload.expenses) ? payload.expenses : [],
    trainers: Array.isArray(payload.trainers) ? payload.trainers : [],
    guestPasses: Array.isArray(payload.guest_passes) ? payload.guest_passes : [],
    branches: Array.isArray(payload.branches) ? payload.branches : [],
    fromRpc: true,
  };
}

/** Load every payment page so Journal / CoA never drop rows at the 20k cap. */
export async function fetchFinanceLedgerAllRpc(gymId) {
  if (!gymId) return null;

  const payments = [];
  const seen = new Set();
  let first = null;
  let page = 1;

  for (;;) {
    const rpc = await fetchFinanceLedgerPageRpc({
      gymId,
      page,
      pageSize: FINANCE_LEDGER_PAGE_SIZE,
      allMatching: false,
    });
    if (!rpc) return first;
    if (!first) first = rpc;

    (rpc.payments || []).forEach((row) => {
      const id = String(row?.id || '').trim();
      if (id) {
        if (seen.has(id)) return;
        seen.add(id);
      }
      payments.push(row);
    });

    const total = Number(rpc.totalPayments) || 0;
    const batch = (rpc.payments || []).length;
    if (!batch) break;
    if (total > 0 && payments.length >= total) break;
    if (batch < FINANCE_LEDGER_PAGE_SIZE) break;
    page += 1;
    if (page > 400) break;
  }

  if (!first) return null;
  return {
    ...first,
    payments,
    totalPayments: Math.max(Number(first.totalPayments) || 0, payments.length),
  };
}
